import { useRef, useState } from "react"
import { useMutation } from "react-apollo"
import { CURRENT_USER_QUERY } from "../me/me"
import CityInput from "../onboarding/city-input"
import PhotoCropper, { cropFractions, prepareImage } from "../onboarding/photo-cropper"
import { HOURS, IDEA_STAGES, INDUSTRIES, INTENTS, SKILLS, STARTUP_STAGES } from "../onboarding/config"
import {
  MAX_HEADLINE,
  MAX_LINKS,
  MAX_PROMPTS,
  MAX_PROMPT_ANSWER,
  PROMPTS,
  editableText,
  linkLabel,
  linksOf,
  promptLabel,
  promptsOf,
} from "./lib"
import { CREATE_IDEA, PROFILE_PAGE_QUERY, UPDATE_IDEA, UPLOAD_PROFILE_PHOTO } from "./queries"
import { IconClose, IconPlus, IconUpload } from "./icons"
import { EditorCard, Modal, Pickable, Spinner, errorText, useSaveSection } from "./ui"
import "../onboarding/onboarding.scss"

const REMOTE = "Remote"

export const NameEditor = ({ me, onDone }) => {
  const [first, setFirst] = useState(me.firstName || "")
  const [last, setLast] = useState(me.lastName || "")
  const { save, busy, error } = useSaveSection()
  const submit = async () => {
    if (await save("name", { firstName: first, lastName: last })) onDone()
  }
  return (
    <EditorCard title="Your name" titleId="fp-name-ed" onSave={submit} onCancel={onDone} busy={busy} error={error}>
      <div className="fp-two">
        <label className="fp-field">
          <span className="fp-label">First name</span>
          <input className="fp-input" value={first} maxLength={50} onChange={(e) => setFirst(e.target.value)} autoComplete="given-name" />
        </label>
        <label className="fp-field">
          <span className="fp-label">Last name</span>
          <input className="fp-input" value={last} maxLength={50} onChange={(e) => setLast(e.target.value)} autoComplete="family-name" />
        </label>
      </div>
      <p className="fp-hint">Other founders see your first name only.</p>
    </EditorCard>
  )
}

export const HeadlineEditor = ({ me, onDone }) => {
  const [value, setValue] = useState(me.headline || "")
  const { save, busy, error } = useSaveSection()
  const submit = async () => {
    if (await save("headline", { headline: value })) onDone()
  }
  return (
    <EditorCard onSave={submit} onCancel={onDone} busy={busy} error={error} titleId="fp-headline-ed">
      <label className="fp-field">
        <span className="fp-label" id="fp-headline-ed">
          Headline
        </span>
        <input
          className="fp-input fp-input--lg"
          value={value}
          maxLength={MAX_HEADLINE}
          placeholder="Building rota software for small hotels"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
        />
      </label>
      <p className="fp-hint">
        One line on what you build or what you're after. {value.length} / {MAX_HEADLINE}
      </p>
    </EditorCard>
  )
}

export const LinksEditor = ({ me, onDone }) => {
  const [links, setLinks] = useState(() => {
    const current = linksOf(me).map((l) => l.url.replace(/^https:\/\//, ""))
    return current.length ? current : [""]
  })
  const { save, busy, error } = useSaveSection()
  const set = (i, v) => setLinks(links.map((l, j) => (j === i ? v : l)))
  const remove = (i) => setLinks(links.length > 1 ? links.filter((_, j) => j !== i) : [""])
  const submit = async () => {
    if (await save("links", { links: links.map((l) => l.trim()).filter(Boolean) })) onDone()
  }
  return (
    <EditorCard
      title="Links"
      titleId="fp-links-ed"
      hint="Up to five. Visitors see the site's name, not the whole address."
      onSave={submit}
      onCancel={onDone}
      busy={busy}
      error={error}
    >
      <div className="fp-stack-sm">
        {links.map((l, i) => (
          <div className="fp-link-row" key={i}>
            <label className="fp-link-label" htmlFor={`fp-link-${i}`}>
              {l.trim() ? linkLabel(/^https?:\/\//.test(l) ? l : `https://${l}`) : "Link"}
            </label>
            <input
              id={`fp-link-${i}`}
              className="fp-input"
              type="url"
              inputMode="url"
              placeholder="Paste a link"
              value={l}
              onChange={(e) => set(i, e.target.value)}
            />
            <button type="button" className="fp-icon-btn" aria-label="Remove link" onClick={() => remove(i)}>
              <IconClose />
            </button>
          </div>
        ))}
        {links.length < MAX_LINKS && (
          <button type="button" className="fp-text-btn" onClick={() => setLinks([...links, ""])}>
            <IconPlus />
            Add another link
          </button>
        )}
      </div>
      <p className="fp-hint">Keep your email and phone number out of these. Founders can message you here.</p>
    </EditorCard>
  )
}

export const AboutEditor = ({ me, onDone }) => {
  const [value, setValue] = useState(editableText(me.whyMakeGoodCofounder))
  const { save, busy, error } = useSaveSection()
  const submit = async () => {
    if (await save("bio", { whyMakeGoodCofounder: value })) onDone()
  }
  return (
    <EditorCard title="About you" titleId="fp-about-ed" onSave={submit} onCancel={onDone} busy={busy} error={error}>
      <label className="fp-field">
        <span className="fp-label">What have you built, what are you looking for, and how much time can you give?</span>
        <textarea className="fp-input fp-textarea" rows={9} maxLength={4000} value={value} onChange={(e) => setValue(e.target.value)} />
      </label>
      <p className="fp-hint">Blank lines become paragraphs. Visitors see about 120 words, then Read more.</p>
    </EditorCard>
  )
}

// All of the owner's prompts at once: answer, swap, add (up to three), remove
export const PromptsEditor = ({ me, startWith, onDone }) => {
  const [items, setItems] = useState(() => {
    const current = promptsOf(me).map((p) => ({ id: p.id, answer: p.answer }))
    if (startWith && !current.some((p) => p.id === startWith) && current.length < MAX_PROMPTS) {
      current.push({ id: startWith, answer: "" })
    }
    return current
  })
  const [picking, setPicking] = useState(items.length === 0 ? "new" : null) // "new" | index being swapped
  const { save, busy, error } = useSaveSection()
  const used = items.map((p) => p.id)

  const choose = (id) => {
    if (picking === "new") setItems([...items, { id, answer: "" }])
    else setItems(items.map((p, i) => (i === picking ? { ...p, id } : p)))
    setPicking(null)
  }
  const submit = async () => {
    const prompts = items.filter((p) => p.answer.trim())
    if (await save("prompts", { prompts })) onDone()
  }

  return (
    <EditorCard
      title="Prompts"
      titleId="fp-prompts-ed"
      hint="Pick up to three. Specific beats modest: a number, a name or a story reads better than “hardworking and reliable”."
      onSave={submit}
      onCancel={onDone}
      busy={busy}
      error={error}
    >
      <div className="fp-stack">
        {items.map((p, i) => (
          <div className="fp-prompt-ed" key={`${p.id}-${i}`}>
            <div className="fp-row fp-row--between">
              <label className="fp-label" htmlFor={`fp-prompt-${i}`}>
                {promptLabel(p.id)}
              </label>
              <div className="fp-row">
                <button type="button" className="fp-text-btn" onClick={() => setPicking(i)}>
                  Swap
                </button>
                <button type="button" className="fp-text-btn" onClick={() => setItems(items.filter((_, j) => j !== i))}>
                  Remove
                </button>
              </div>
            </div>
            <textarea
              id={`fp-prompt-${i}`}
              className="fp-input fp-textarea fp-textarea--prompt"
              rows={3}
              maxLength={MAX_PROMPT_ANSWER}
              value={p.answer}
              onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, answer: e.target.value } : x)))}
            />
            <span className="fp-hint">
              {p.answer.length} / {MAX_PROMPT_ANSWER}
            </span>
          </div>
        ))}
        {picking !== null && (
          <div className="fp-prompt-list" role="group" aria-label="Pick a prompt">
            {PROMPTS.filter((x) => !used.includes(x.id) || (picking !== "new" && items[picking].id === x.id)).map((x) => (
              <button type="button" key={x.id} className="fp-prompt-option fp-pickable" onClick={() => choose(x.id)}>
                {x.label}
              </button>
            ))}
            {items.length > 0 && (
              <button type="button" className="fp-text-btn" onClick={() => setPicking(null)}>
                Never mind
              </button>
            )}
          </div>
        )}
        {picking === null && items.length < MAX_PROMPTS && (
          <button type="button" className="fp-text-btn" onClick={() => setPicking("new")}>
            <IconPlus />
            Add a prompt
          </button>
        )}
      </div>
    </EditorCard>
  )
}

const itemIds = (list) => (list || []).map((x) => x.itemId)
const toggleMax2 = (list, id) =>
  list.includes(id) ? list.filter((x) => x !== id) : list.length >= 2 ? [list[1], id] : [...list, id]
const same = (a, b) => a.length === b.length && a.every((x) => b.includes(x))

// What they bring and need, why they're here, time and place, startup details
export const BasicsEditor = ({ me, onDone }) => {
  const [intents, setIntents] = useState(() => Object.fromEntries(INTENTS.map((i) => [i.field, Boolean(me[i.field])])))
  const [bring, setBring] = useState(itemIds(me.hasSkills))
  const [need, setNeed] = useState(itemIds(me.wantsSkills))
  const [hours, setHours] = useState(me.hoursPerWeekAvailable ? me.hoursPerWeekAvailable.itemId : null)
  const startRemote = me.location === REMOTE
  const [remote, setRemote] = useState(startRemote)
  const [city, setCity] = useState(
    me.location && !startRemote ? { label: me.location, lat: me.locationLat, lng: me.locationLng } : null
  )
  const [stage, setStage] = useState(me.startupStage ? me.startupStage.itemId : null)
  const [industry, setIndustry] = useState(me.startupIndustry ? me.startupIndustry.itemId : null)
  const [url, setUrl] = useState(me.ownStartupUrl || "")
  const { save, busy, error, setError } = useSaveSection()

  const submit = async () => {
    if (!Object.values(intents).some(Boolean)) return setError("Pick at least one reason you're here.")
    if (!bring.length || !need.length) return setError("Pick one or two skills you bring and one or two you need.")
    if (!hours) return setError("Pick how many hours a week you can give.")
    if (!remote && !city) return setError("Tell us your city, or choose remote.")
    const steps = []
    if (INTENTS.some((i) => intents[i.field] !== Boolean(me[i.field]))) steps.push(["intent", intents])
    if (!same(bring, itemIds(me.hasSkills))) steps.push(["bring", { hasSkills: bring }])
    if (!same(need, itemIds(me.wantsSkills))) steps.push(["need", { wantsSkills: need }])
    const placeChanged =
      remote !== startRemote ||
      (!remote && city && city.label !== me.location) ||
      hours !== (me.hoursPerWeekAvailable && me.hoursPerWeekAvailable.itemId)
    if (placeChanged) {
      steps.push([
        "where",
        remote
          ? { remote: true, hoursPerWeekAvailable: hours }
          : { location: city.label, locationLat: city.lat, locationLng: city.lng, hoursPerWeekAvailable: hours },
      ])
    }
    if (intents.hasOwnStartup) {
      const startupChanged =
        stage !== (me.startupStage && me.startupStage.itemId) ||
        industry !== (me.startupIndustry && me.startupIndustry.itemId) ||
        url !== (me.ownStartupUrl || "")
      if (startupChanged) steps.push(["startup", { startupStage: stage, startupIndustry: industry, ownStartupUrl: url }])
    }
    for (const [section, input] of steps) {
      if (!(await save(section, input))) return undefined
    }
    return onDone()
  }

  return (
    <EditorCard title="The basics" titleId="fp-basics-ed" onSave={submit} onCancel={onDone} busy={busy} error={error}>
      <fieldset className="fp-fieldset">
        <legend className="fp-label">Here to</legend>
        <div className="fp-picks">
          {INTENTS.map((i) => (
            <Pickable key={i.key} on={intents[i.field]} onClick={() => setIntents({ ...intents, [i.field]: !intents[i.field] })}>
              {i.label}
            </Pickable>
          ))}
        </div>
      </fieldset>
      <fieldset className="fp-fieldset">
        <legend className="fp-label">You bring (up to two)</legend>
        <div className="fp-picks">
          {SKILLS.map((s) => (
            <Pickable key={s.itemId} on={bring.includes(s.itemId)} onClick={() => setBring(toggleMax2(bring, s.itemId))}>
              {s.label}
            </Pickable>
          ))}
        </div>
      </fieldset>
      <fieldset className="fp-fieldset">
        <legend className="fp-label">You're looking for (up to two)</legend>
        <div className="fp-picks">
          {SKILLS.map((s) => (
            <Pickable key={s.itemId} on={need.includes(s.itemId)} onClick={() => setNeed(toggleMax2(need, s.itemId))}>
              {s.label}
            </Pickable>
          ))}
        </div>
      </fieldset>
      <fieldset className="fp-fieldset">
        <legend className="fp-label">Hours a week</legend>
        <div className="fp-picks">
          {HOURS.map((h) => (
            <Pickable key={h.itemId} on={hours === h.itemId} onClick={() => setHours(h.itemId)}>
              {h.label}
            </Pickable>
          ))}
        </div>
      </fieldset>
      <div className="fp-fieldset">
        <label className="fp-label" htmlFor="ob-city">
          Where you're based
        </label>
        <CityInput
          city={city}
          remote={remote}
          onPick={(c) => {
            setCity(c)
            setRemote(false)
          }}
          onRemote={() => {
            setRemote(true)
            setCity(null)
          }}
          onClear={() => {
            setRemote(false)
            setCity(null)
          }}
        />
      </div>
      {intents.hasOwnStartup && (
        <fieldset className="fp-fieldset">
          <legend className="fp-label">Your startup</legend>
          <div className="fp-picks">
            {STARTUP_STAGES.map((s) => (
              <Pickable key={s.itemId} on={stage === s.itemId} onClick={() => setStage(s.itemId)}>
                {s.label}
              </Pickable>
            ))}
          </div>
          <div className="fp-two">
            <label className="fp-field">
              <span className="fp-label">Industry</span>
              <select className="fp-input" value={industry || ""} onChange={(e) => setIndustry(Number(e.target.value) || null)}>
                <option value="">Pick one</option>
                {INDUSTRIES.map((i) => (
                  <option key={i.itemId} value={i.itemId}>
                    {i.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="fp-field">
              <span className="fp-label">Website (optional)</span>
              <input className="fp-input" type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="yourstartup.com" />
            </label>
          </div>
        </fieldset>
      )}
    </EditorCard>
  )
}

// New idea, an existing one, or a name for the pitch from sign-up
export const IdeaEditor = ({ idea, fromPitch, me, onDone }) => {
  const seed = idea || fromPitch || {}
  const [title, setTitle] = useState(seed.title || "")
  const [pitch, setPitch] = useState(editableText(seed.elevatorPitch || ""))
  const [stage, setStage] = useState(seed.ideaStage ? seed.ideaStage.itemId : null)
  const [industry, setIndustry] = useState(seed.ideaIndustry ? seed.ideaIndustry.itemId : null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const refetchQueries = [
    { query: CURRENT_USER_QUERY },
    { query: PROFILE_PAGE_QUERY, variables: { id: me.id, loggedInUserId: me.id } },
  ]
  const [create] = useMutation(CREATE_IDEA, { refetchQueries, awaitRefetchQueries: true })
  const [update] = useMutation(UPDATE_IDEA, { refetchQueries, awaitRefetchQueries: true })

  const submit = async () => {
    if (!title.trim()) return setError("Give your idea a name.")
    if (!pitch.trim()) return setError("Describe the idea in a sentence or two.")
    if (!stage || !industry) return setError("Pick a stage and an industry.")
    setBusy(true)
    setError("")
    const variables = { title: title.trim(), elevatorPitch: pitch.trim(), ideaStage: stage, ideaIndustry: industry }
    try {
      if (idea) await update({ variables: { id: idea.id, ...variables } })
      else await create({ variables })
      return onDone()
    } catch (e) {
      setBusy(false)
      return setError(errorText(e))
    }
  }

  return (
    <EditorCard
      title={idea ? "Edit your idea" : fromPitch ? "Name your idea and post it" : "Post an idea"}
      titleId="fp-idea-ed"
      hint={idea ? null : "It goes in the Ideas feed, where founders can like it and comment."}
      onSave={submit}
      onCancel={onDone}
      busy={busy}
      error={error}
      saveLabel={idea ? "Save" : "Post it"}
    >
      <label className="fp-field">
        <span className="fp-label">Name</span>
        <input className="fp-input" value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} placeholder="Shift scheduling for small hotels" />
      </label>
      <label className="fp-field">
        <span className="fp-label">The idea</span>
        <textarea className="fp-input fp-textarea" rows={5} maxLength={4000} value={pitch} onChange={(e) => setPitch(e.target.value)} />
      </label>
      <fieldset className="fp-fieldset">
        <legend className="fp-label">Stage</legend>
        <div className="fp-picks">
          {IDEA_STAGES.map((s) => (
            <Pickable key={s.itemId} on={stage === s.itemId} onClick={() => setStage(s.itemId)}>
              {s.label}
            </Pickable>
          ))}
        </div>
      </fieldset>
      <label className="fp-field">
        <span className="fp-label">Industry</span>
        <select className="fp-input" value={industry || ""} onChange={(e) => setIndustry(Number(e.target.value) || null)}>
          <option value="">Pick one</option>
          {INDUSTRIES.map((i) => (
            <option key={i.itemId} value={i.itemId}>
              {i.label}
            </option>
          ))}
        </select>
      </label>
    </EditorCard>
  )
}

export const useIdeaVisibility = (me) => {
  const [update, { loading }] = useMutation(UPDATE_IDEA, {
    refetchQueries: [
      { query: CURRENT_USER_QUERY },
      { query: PROFILE_PAGE_QUERY, variables: { id: me.id, loggedInUserId: me.id } },
    ],
    awaitRefetchQueries: true,
  })
  return { setActive: (id, isActive) => update({ variables: { id, isActive } }), loading }
}

export const PhotoEditor = ({ onClose }) => {
  const [image, setImage] = useState(null)
  const [crop, setCrop] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [upload] = useMutation(UPLOAD_PROFILE_PHOTO)
  const fileInput = useRef(null)

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = ""
    if (!file) return
    setError("")
    try {
      setImage(await prepareImage(file))
    } catch (err) {
      setError(err.message)
    }
  }
  const submit = async () => {
    if (!image || !crop) return
    setBusy(true)
    setError("")
    try {
      await upload({ variables: { dataUrl: image.src, crop: cropFractions(crop) } })
      onClose()
    } catch (e) {
      setBusy(false)
      setError(errorText(e))
    }
  }

  return (
    <Modal title="Your photo" titleId="fp-photo-ed" onClose={onClose}>
      <div className="fp-photo-ed">
        {image ? (
          <PhotoCropper image={image} onChange={setCrop} />
        ) : (
          <p className="fp-hint">A clear photo of your face works best. JPG or PNG.</p>
        )}
      </div>
      <button type="button" className="fp-btn fp-btn--outline fp-btn--block" onClick={() => fileInput.current.click()}>
        <IconUpload />
        {image ? "Choose another photo" : "Choose a photo"}
      </button>
      <input ref={fileInput} type="file" accept="image/jpeg,image/png" className="fp-visually-hidden" onChange={onFile} tabIndex={-1} />
      {error && (
        <p className="fp-error" role="alert">
          {error}
        </p>
      )}
      <div className="fp-editor-foot">
        <span className="fp-grow" />
        <button type="button" className="fp-btn fp-btn--ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="fp-btn" onClick={submit} disabled={busy || !image}>
          {busy ? <Spinner /> : null}
          {busy ? "Uploading…" : "Save photo"}
        </button>
      </div>
    </Modal>
  )
}
